import { TimelineItem } from "../interfaces/IPatient";

const WORKFLOW_STAGE_KEYS = new Set([
  "BILL_AUDIT",
  "CPD",
  "EXPERT",
  "APPROVER",
  "AUDITOR",
  "CMO",
  "CMD",
]);

const WORKFLOW_STAGE_LABELS: Record<string, string> = {
  BILL_AUDIT: "Bill Audit",
  CPD: "CPD",
  EXPERT: "Expert",
  APPROVER: "Approver",
  AUDITOR: "Auditor",
  CMO: "CMO",
  CMD: "CMO",
};

const ACTION_LABELS: Record<string, string> = {
  APPROVE: "Approved",
  APPROVED: "Approved",
  REJECT: "Rejected",
  REJECTED: "Rejected",
  QUERY: "Query",
  "QUERY RAISED": "Query",
  "NMI RAISED": "Query",
  "SEEK OPINION": "Seek Opinion",
  "QUERY REPLIED": "Query Replied",
  "NMI REPLIED": "Query Replied",
  "QUERY CLOSED": "Query Closed",
  SUBMITTED: "Submitted",
  SAVED: "Pending",
  "PENDING REVIEW": "Pending",
  "DOCUMENT APPROVED": "Document Approved",
  "DOCUMENT REJECTED": "Document Rejected",
  "REFERRAL INITIATED": "Referral Initiated",
  "REFERRAL ACCEPTED": "Referral Accepted",
  "IN REVIEW - CPD": "IN REVIEW - CPD",
  "IN REVIEW - APPROVER": "IN REVIEW - APPROVER",
  "IN REVIEW - AUDITOR": "IN REVIEW - AUDITOR",
  "IN REVIEW - CMO": "IN REVIEW - CMO",
  SETTLED: "SETTLED",
};

const HSP_CLAIM_PROGRESS_STAGE_KEYS = new Set([
  "HSP_CLAIM_PROGRESS",
  "HSP CLAIM PROGRESS",
]);

const toKey = (value?: string | null) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

const titleCase = (value: string) =>
  value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const formatTimelineAction = (
  status?: string | null,
  stateName?: string | null,
  visitStageName?: string | null,
): string => {
  const raw = String(status || stateName || "").trim();
  if (!raw) return "Action";

  const upperRaw = raw.toUpperCase().replace(/\s+/g, " ");
  // HSP claim-progress rows store exact labels — keep them as-is.
  if (upperRaw.startsWith("IN REVIEW")) {
    return upperRaw;
  }
  if (isHspClaimProgressStage(visitStageName)) {
    if (upperRaw === "APPROVED") return "APPROVED";
    if (upperRaw === "SETTLED") return "SETTLED";
  }

  const key = toKey(raw);
  const stageKey = toKey(visitStageName);
  if (
    (key === "QUERY" || key === "QUERY_RAISED" || key === "NMI_RAISED") &&
    WORKFLOW_STAGE_KEYS.has(stageKey) &&
    raw.toLowerCase().includes("seek opinion")
  ) {
    return "Seek Opinion";
  }
  return ACTION_LABELS[key] || titleCase(raw);
};

export const formatTimelineStageLabel = (visitStageName?: string | null): string => {
  const stage = String(visitStageName || "").trim();
  if (!stage) return "";

  const key = toKey(stage);
  if (WORKFLOW_STAGE_LABELS[key]) {
    return WORKFLOW_STAGE_LABELS[key];
  }

  return titleCase(stage);
};

export const isCmoTimelineStage = (visitStageName?: string | null): boolean => {
  const stageKey = toKey(visitStageName);
  return stageKey === "CMO" || stageKey === "CMD";
};

const isHspClaimProgressStage = (visitStageName?: string | null): boolean => {
  const key = toKey(visitStageName);
  return (
    HSP_CLAIM_PROGRESS_STAGE_KEYS.has(key) ||
    key.includes("HSP_CLAIM_PROGRESS")
  );
};

export const formatTimelineContext = (
  visitStageName?: string | null,
  actionLabel?: string,
): string => {
  const stageLabel = formatTimelineStageLabel(visitStageName);
  if (!stageLabel) return "";

  const actionKey = toKey(actionLabel);
  const stageKey = toKey(visitStageName);

  if (isHspClaimProgressStage(visitStageName)) {
    return "";
  }

  if (actionKey.includes("DOCUMENT")) {
    return stageLabel;
  }

  if (WORKFLOW_STAGE_KEYS.has(stageKey)) {
    return `${stageLabel} Review`;
  }

  if (stageKey === "SETTLEMENT") {
    return "Settlement";
  }

  return stageLabel;
};

export type TimelineDisplay = {
  context: string;
  action: string;
  actor?: string;
  actorRole?: string;
  remarks?: string;
  actionTone: "approved" | "rejected" | "query" | "submitted" | "neutral";
};

export const getTimelineActionTone = (
  actionLabel: string,
): TimelineDisplay["actionTone"] => {
  const key = toKey(actionLabel);
  if (
    key.includes("APPROVED") ||
    key === "APPROVE" ||
    key === "SETTLED" ||
    key.includes("IN_REVIEW")
  ) {
    return key.includes("IN_REVIEW") ? "submitted" : "approved";
  }
  if (key.includes("REJECTED") || key === "REJECT") {
    return "rejected";
  }
  if (key.includes("QUERY") || key === "QUERY") {
    return "query";
  }
  if (key === "SUBMITTED" || key === "SAVED") {
    return "submitted";
  }
  return "neutral";
};

export const ACTION_TONE_COLORS: Record<TimelineDisplay["actionTone"], string> = {
  approved: "#2E8B57",
  rejected: "#C62828",
  query: "#D97706",
  submitted: "#004C8F",
  neutral: "#1F2937",
};

export type BuildTimelineDisplayOptions = {
  /** HSP timeline: omit "CMO Review —" prefix on CMO workflow entries. */
  hospitalViewer?: boolean;
};

export const buildTimelineDisplay = (
  item: TimelineItem,
  options?: BuildTimelineDisplayOptions,
): TimelineDisplay => {
  const action = formatTimelineAction(item.status, item.stateName, item.visitStageName);
  const isHospitalProgress =
    options?.hospitalViewer &&
    (isHspClaimProgressStage(item.visitStageName) ||
      String(action || "")
        .toUpperCase()
        .startsWith("IN REVIEW"));

  const context = isHospitalProgress
    ? ""
    : options?.hospitalViewer && isCmoTimelineStage(item.visitStageName)
      ? ""
      : formatTimelineContext(item.visitStageName, action);

  return {
    context,
    action,
    actor: item.userName?.trim() || undefined,
    actorRole: item.userRole?.trim() || undefined,
    // Remarks intentionally omitted from timeline UI.
    remarks: undefined,
    actionTone: getTimelineActionTone(action),
  };
};
